import type { DiscountType, InvoiceLine, InvoiceLineTaxDetail } from "@/types/invoice";
import type Withholding from "@/types/withholding";

import {
  DISCOUNT_VALUE,
  getDiscountAmount,
  getDiscountPercentage,
  getLineNet,
  isValueDiscount,
} from "@/utils/invoiceLineNet";
import { isIvaTax, projectTaxes, withholdingFamily } from "@/utils/taxes";
import { resolveWithholdingAmount } from "@/utils/withholdings";

export { getLineNet };

/**
 * Pure recomputation helpers for a single invoice line in the invoice form.
 *
 * These live outside the component so the money math (which value follows which
 * when the user edits a line) is unit-testable without mounting Vuetify. The
 * component (`ItemsTable.vue`) owns WHEN to call them; this module owns HOW the
 * derived values are computed. All functions mutate the line in place, matching
 * how the reactive Options-API component expects to work with `this.lines`.
 */

/**
 * A withholding carries an internal, non-persisted flag describing whether its
 * base follows the net/IVA. Decide once at load; an explicit base edit sets it
 * to false. Payload serializers omit it.
 */
export type TrackableWithholding = Withholding & {
  _baseTracksNet?: boolean;
  /** Last implicit base, so a tax editor's amount update is distinguishable from a name edit. */
  _trackedBase?: number;
};

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function approxEqual(a: number, b: number, decimals: number): boolean {
  return roundTo(a, decimals) === roundTo(b, decimals);
}

/** Sum of tax_amount over the line's IVA taxes (used as the base for ReteIva). */
export function getLineIva(line: Pick<InvoiceLine, "tax_details">, country?: string | null): number {
  return (line.tax_details || [])
    .filter((tax) => isIvaTax(tax, country))
    .reduce((sum, tax) => sum + (tax.tax_amount || 0), 0);
}

/** Taxable base a withholding applies to: the line IVA for ReteIva, else the net. */
export function withholdingBase(
  withholding: Pick<Withholding, "tax_type" | "tax_type_code">,
  net: number,
  iva: number,
  country?: string | null,
): number {
  return withholdingFamily(withholding, country) === "ReteIva" ? iva : net;
}

/**
 * Decide, once at load, whether each line withholding's base should follow the
 * line net going forward. A withholding with no base, or whose loaded base
 * matches the current net/iva base, tracks; one whose base already diverges is
 * treated as an intentional override and stays frozen. Mutates each withholding.
 */
export function initWithholdingBaseTracking(
  line: InvoiceLine,
  decimals = 2,
  discountType?: DiscountType,
  country?: string | null,
): void {
  const projected = projectTaxes(line.tax_details || [], line.withholdings || [], country);
  line.tax_details = projected.taxes;
  line.withholdings = projected.withholdings.map((w, index) => Object.assign(line.withholdings?.[index] || {}, w));
  const net = getLineNet(line, discountType);
  const iva = getLineIva(line, country);
  initWithholdingBaseTrackingForTotals(line.withholdings || [], net, iva, decimals, country);
}

/** Same provenance decision for line and document bases; never reset an existing decision. */
export function initWithholdingBaseTrackingForTotals(
  withholdings: TrackableWithholding[],
  net: number,
  iva: number,
  decimals = 2,
  country?: string | null,
): void {
  withholdings.forEach(w => {
    w._trackedBase ??= withholdingBase(w, net, iva, country);
    if (w._baseTracksNet !== undefined) return;
    if (w.taxable_amount === undefined || w.taxable_amount === null) {
      w._baseTracksNet = true;
      return;
    }
    w._baseTracksNet = w.taxable_amount !== 0 && approxEqual(w.taxable_amount, withholdingBase(w, net, iva, country), decimals);
  });
}

/**
 * Recompute every value derived from a line's net, in dependency order:
 *   taxes -> line IVA -> withholdings -> line total.
 *
 * `resetTaxBaseToNet` controls whether each tax's `taxable_amount` is snapped
 * back to the line net. Pass `true` when the net itself changed (editing
 * unit_price / quantity / discount / total) — taxes are a percentage of the
 * net. Pass `false` when only the taxes changed (add / remove / edit in the
 * tax editor) so a base the user typed for a specific tax is preserved.
 *
 * Withholdings that track follow the current base; frozen ones keep their base.
 * A saved amount is preserved unless its derived base changes. Missing amounts
 * are calculated from the rate. The document separately deducts header retentions.
 */
export function recalcLineDerived(
  line: InvoiceLine,
  decimals = 2,
  options: { resetTaxBaseToNet?: boolean; preserveTaxAmounts?: boolean; discountType?: DiscountType; country?: string | null } = {},
): void {
  const { resetTaxBaseToNet = false, discountType, country } = options;
  const net = getLineNet(line, discountType);
  initWithholdingBaseTracking(line, decimals, discountType, country);

  (line.tax_details || []).forEach((tax: InvoiceLineTaxDetail) => {
    if (options.preserveTaxAmounts || tax.aiu_details || (!tax.percentage && tax.tax_amount)) return;
    if (resetTaxBaseToNet) tax.taxable_amount = net;
    tax.tax_amount = roundTo((tax.taxable_amount || 0) * (tax.percentage || 0) / 100, decimals);
  });

  const totalTax = (line.tax_details || []).reduce((sum, tax) => sum + (tax.tax_amount || 0), 0);

  const iva = getLineIva(line, country);
  (line.withholdings || []).forEach((w: TrackableWithholding) => {
    const previousBase = w.taxable_amount ?? w._trackedBase;
    const nextBase = withholdingBase(w, net, iva, country);
    // Missing base is not evidence of a financial change. A name-only edit must
    // preserve a saved amount; a net edit or a changed IVA base may recompute it.
    const baseChanged = w._baseTracksNet && nextBase !== previousBase;
    if (baseChanged || (w._baseTracksNet && w.tax_amount == null)) {
      w.taxable_amount = nextBase;
    }
    if (w.percentage != null && (w.tax_amount == null || baseChanged)) {
      w.tax_amount = roundTo(resolveWithholdingAmount({ ...w, tax_amount: undefined }, net, iva, country), decimals);
    }
    w._trackedBase = nextBase;
  });

  line.total = roundTo(net + totalTax - (line.withholdings || []).reduce(
    (sum, w) => sum + resolveWithholdingAmount(w, net, iva, country), 0), decimals);
}

export function recalcLineTotal(line: InvoiceLine, discountType?: DiscountType, country?: string | null): void {
  const projected = projectTaxes(line.tax_details || [], line.withholdings || [], country);
  const net = getLineNet(line, discountType);
  const iva = getLineIva({ tax_details: projected.taxes }, country);
  line.total = net + projected.taxes.reduce((sum, tax) => sum + (tax.tax_amount || 0), 0)
    - projected.withholdings.reduce((sum, w) => sum + resolveWithholdingAmount(w, net, iva, country), 0);
}

/**
 * Solve unit_price from total = net * effectiveRate + fixedAmounts, including
 * both additive taxes and retentions (ReteIVA also applies to fixed IVA). Callers
 * should follow with `recalcLineDerived(line, decimals, { resetTaxBaseToNet: true })`
 * to refresh each tax's base and amount against the new net, keeping
 * net + taxes - retentions == total consistent. Mutates `line.unit_price`.
 */
export function backSolveUnitPriceFromTotal(line: InvoiceLine, discountType?: DiscountType, country?: string | null): boolean {
  const total = line.total || 0;
  const quantity = line.quantity ?? 1;
  if (!Number.isFinite(quantity) || quantity === 0) return false;
  const projected = projectTaxes(line.tax_details || [], line.withholdings || [], country);
  if (projected.taxes.some(tax => tax.aiu_details)) return false;
  const taxRate = projected.taxes.reduce((sum, tax) => sum + (tax.percentage || 0) / 100, 0);
  const ivaRate = projected.taxes.filter(tax => isIvaTax(tax, country))
    .reduce((sum, tax) => sum + (tax.percentage || 0) / 100, 0);
  const fixedIva = getLineIva({ tax_details: projected.taxes.filter(tax => !tax.percentage) }, country);
  let fixed = projected.taxes.filter(tax => !tax.percentage).reduce((sum, tax) => sum + (tax.tax_amount || 0), 0);
  let rate = 1 + taxRate;
  for (const w of projected.withholdings as TrackableWithholding[]) {
    if (w._baseTracksNet && w.percentage != null) {
      const isReteIva = withholdingFamily(w, country) === "ReteIva";
      rate -= w.percentage / 100 * (isReteIva ? ivaRate : 1);
      if (isReteIva) fixed -= fixedIva * w.percentage / 100;
    } else {
      fixed -= resolveWithholdingAmount(w, getLineNet(line, discountType), getLineIva(line, country), country);
    }
  }
  if (!Number.isFinite(rate) || Math.abs(rate) < 1e-10) return false;
  const net = (total - fixed) / rate;

  if (isValueDiscount(discountType)) {
    // The amount the user typed is a fixed number of pesos off this line, so it
    // is the discount that stays put and the unit price that moves.
    line.unit_price = quantity !== 0 ? (net + getDiscountAmount(line, discountType)) / quantity : 0;
    return true;
  }

  const denominator = quantity * (1 - (line.discount || 0) / 100);
  if (!Number.isFinite(denominator) || Math.abs(denominator) < 1e-10) return false;
  line.unit_price = net / denominator;
  return true;
}

/**
 * Rewrite every line so the invoice's discounts mean the same thing under the new
 * mode, keeping each line total where it is. Without this, a "10" meaning 10%
 * would silently start meaning $10.
 *
 * The mode belongs to the invoice, so the whole document converts at once: the
 * backend cannot represent a mixed invoice and neither can Siigo.
 *
 * Going back to PERCENTAGE the derived percentage is NOT rounded. Rounding it to
 * two decimals is the cents bug in reverse — it is what makes the amount the
 * backend derives back from it drift.
 */
export function convertLinesToDiscountType(
  lines: InvoiceLine[],
  from: DiscountType,
  to: DiscountType,
): void {
  if (from === to) return;

  lines.forEach((line) => {
    line.discount = to === DISCOUNT_VALUE
      ? getDiscountAmount(line, from)
      : getDiscountPercentage(line, from);
  });
}
